<template>
    <form @submit.prevent="onSubmit" ref="form" action="/settings/profile" method="post" enctype="multipart/form-data" :disabled="!cardSetupComplete">
        <input type="hidden" name="signup_type" v-model="form.signup_type">
        <input v-show="form.existing_user_id" type="hidden" name="existing_user_id" v-model="form.existing_user_id">

        <div v-show="errors.length > 0" class="alert alert-bad">
            <ul>
                <li v-for="error in errors" :key="error"><span class="fa fa-flag"></span>{{ error }}</li>
            </ul>
        </div>

        <div class="general-title">
            <div class="title">Account Profile</div>
        </div>
        <div class="input-container">
            <div class="input-title">Account Name</div>
            <div class="input-box"><input type="text" name="account_name" :placeholder="accountPlaceholder" v-model="form.account_name"></div>
        </div>
        <photo-upload name="account_avatar" title="Account Avatar" preview-title="Avatar To Upload"></photo-upload>
        <br>
        <div v-show="isBillable">
            <div class="tool-tip add-buffer">
                <div class="left">
                    <div class="headline">Your Free Trial</div>
                    You will not be charged until {{ firstBillingDate }}, when your 30-day trial ends. You may cancel your account on any date before that.
                </div>
            </div>
            <div class="input-container">
                <div class="input-title">Select Your Country</div>
                <div class="input-box">
                    <selectize v-model="form.billing_country" name="billing_country" placeholder="Select Country" required>
                        <option v-for="country in countries" :key="country.value" :value="country.value">{{ country.name }}</option>
                    </selectize>
                </div>
            </div>
            <div v-show="form.billing_country === 'USA'" class="input-container">
                <div class="input-title">Select Your State</div>
                <div class="input-box">
                    <selectize v-model="form.billing_state" name="billing_state" placeholder="Select State" required>
                        <option v-for="state in states" :key="state.value" :value="state.value">{{ state.name }}</option>
                    </selectize>
                </div>
            </div>

            <input type="hidden" name="billing_zip" v-model="form.billing_zip">

            <card-form @completed="cardCompleted"></card-form>
        </div><br>

        <div v-show="canShowBillingInfo" class="general-title">
            <div class="title">Choose Account Type</div>
        </div>

        <div v-show="canShowBillingInfo" class="input-container">
            <div class="input-title">How Many Children?</div>
            <label class="input-radio">
                <input type="radio" name="max_children" value="4" v-model="form.max_children">
                <span class="radio-checkmark"></span>
                <div class="radio-text">Up to four children</div>
            </label>
            <label class="input-radio">
                <input type="radio" name="max_children" value="6" v-model="form.max_children">
                <span class="radio-checkmark"></span>
                <div class="radio-text">Up to six children</div>
            </label>
        </div>
        <div v-show="canShowBillingInfo" class="input-container">
            <div class="input-title">Choose Billing Rate</div>
            <label class="input-radio">
                <input type="radio" name="billing_rate" value="YEARLY" v-model="form.billing_rate">
                <span class="radio-checkmark"></span>
                <div class="radio-text">${{ yearlyPricing }} {{ calculatedTax(yearlyPricing) }} per year</div>
            </label>
            <label class="input-radio">
                <input type="radio" name="billing_rate" value="MONTHLY"  v-model="form.billing_rate">
                <span class="radio-checkmark"></span>
                <div class="radio-text">${{ monthlyPricing }} {{ calculatedTax(monthlyPricing) }} per month</div>
            </label>
        </div>

        <div v-show="errors.length > 0" class="alert alert-bad">
            <ul>
                <li><span class="fa fa-flag"></span>There were problems with your form above. Please scroll up to correct them and then try submitting the form again.</li>
            </ul>
        </div>

        <button v-show="!loading" type="submit" class="submit-standard good-bg" :disabled="!canShowBillingInfo && false">Create Account<span class="fa fa-check"></span></button>
        <button v-show="loading" type="submit" class="submit-standard neutral-bg" disabled>Processing...</button>

    </form>
</template>

<script>

import moment from 'moment';
import axios from 'axios';
import countries from '@/utils/countries';
import states from '@/utils/states';
import apiErrors from '@/utils/apiErrors';

export default {

    props: {
        type: {
            type: String,
            default: 'NEW',
        },
        redirectUrl: {
            type: String,
            default: '/dashboard',
        },
        userId: {
            type: Number,
            default: null,
        },
    },

    data() {
        return {
            loading: false,
            errors: [],
            states,
            countries,
            form: {
                signup_type: this.type,
                max_children: 4,
                billing_country: 'USA',
                billing_rate: 'YEARLY',
                existing_user_id: this.userId,
            },
            taxRate: 0,
            cardSetupComplete: false,
        }
    },

    computed: {

        isBillable() {
            return this.type === 'STANDARD' || this.type === 'NEW';
        },

        firstBillingDate() {
            return moment().add(90, 'days').format('MMMM D, YYYY')
        },

        yearlyPricing() {
            return parseInt(this.form.max_children, 10) === 4 ? 100 : 150;
        },

        monthlyPricing() {
            return parseInt(this.form.max_children, 10) === 4 ? 10 : 15;
        },

        accountPlaceholder() {
            if (this.form.last_name && this.form.last_name.length > 0) {
                return 'Ex: ' + this.form.last_name + ' Family';
            }

            return 'Ex: Your Family';
        },

        canShowBillingInfo() {
            if (this.isBillable) {
                if (this.form.billing_country != 'USA') {
                    return this.cardSetupComplete;
                }

                return this.cardSetupComplete && !!this.form.billing_state;
            }

            return false;
        }

    },

    watch: {
        'form.billing_state': async function(newValue) {
            this.getTaxRate();
        },

        'form.billing_zip': async function(newValue) {
            this.getTaxRate();
        },
    },

    methods: {

        cardCompleted(token) {
            this.form.token = token;
            this.form.billing_zip = token.card.address_zip;
            this.cardSetupComplete = true;

            this.getTaxRate();
        },

        async getTaxRate() {
            if (!this.form.billing_state || !this.form.billing_zip) {
                this.taxRate = 0;
                return;
            }

            const { data } = await axios.post('/tax-rate/', { state: this.form.billing_state, zip: this.form.billing_zip } );

            if (data.success) {
                this.taxRate = data.rate;
            }
        },

        calculatedTax(amount) {
            if (this.taxRate > 0) {
                return ' + $' + parseFloat(amount * this.taxRate).toFixed(2) + ' sales tax ';
            }

            return '';
        },

        async onSubmit() {
            this.loading = true;
            this.errors = [];

            try {
                const { data } = await axios.post('/account/create', new FormData(this.$refs.form));

                if (data.success) {
                    this.handleSuccessfulSubscription();
                }
            } catch (e) {
                if (e.response && e.response.data && e.response.data.errors) {
                    Object.values(e.response.data.errors).forEach(errors => {
                        errors.forEach(error => {
                            this.errors.push(error);
                        });
                    });

                    document.querySelector('html').scrollTop = 0;
                } else {
                    this.errors = [ apiErrors.getErrorMessage(e) ];

                    document.querySelector('html').scrollTop = 0;
                }
            }

            this.loading = false;
        },

        handleSuccessfulSubscription() {
            return window.location.href = this.redirectUrl;
        },
    }
}

</script>



// WEBPACK FOOTER //
// resources/js/components/signup/CreateAnotherAccountForm.vue